import type {Ref} from 'react'
import * as React from 'react'
import {useCallback, useRef} from 'react'

import {noop} from '../utils/empty'

const TIMEOUT_MS = 500

export default function useSearchInput(
  defaultValue = '',
  onChangeExternal: (arg0: string) => unknown = noop,
): {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  focusInput: () => void
  onClear: () => void
  inputRef: Ref<HTMLInputElement>
} {
  const changeTextTimeoutRef = useRef<number>()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [value, setValue] = React.useState<string>(defaultValue)

  React.useEffect(() => clearTimeout(changeTextTimeoutRef.current), [])

  const onChangeExternalWithDelay = useCallback(
    (data: string) => {
      clearTimeout(changeTextTimeoutRef.current)
      changeTextTimeoutRef.current = window.setTimeout(() => onChangeExternal(data), TIMEOUT_MS)
    },
    [onChangeExternal],
  )

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.currentTarget.value
      setValue(newValue)
      onChangeExternalWithDelay(newValue)
    },
    [setValue, onChangeExternalWithDelay],
  )

  const focusInput = React.useCallback(() => {
    inputRef.current?.focus()
  }, [inputRef])

  const onClear = React.useCallback(() => {
    setValue('')
    onChangeExternal('')
  }, [setValue, onChangeExternal])

  return {
    value,
    onChange,
    focusInput,
    onClear,
    inputRef,
  }
}
